import { JSX } from 'react';
import { FullName, LocationInfo, FileDataViewModel } from 'src/api/common';
import { ContactAgent, Experience, LanguageLevel } from 'src/api/person-profile/types';

// What the roles and skills upsert endpoints accept — a step that sends more is rejected, so
// anything that grows the selection (the person, the autofill) has to respect them.
export const MAX_ROLES = 3;
export const MAX_SKILLS_PER_ROLE = 30;

export interface OnBoardingPersonProfile {
  userId: string;
  id: string;
  fullName: FullName;
  roles: WorkRole[];
  skills: WorkRoleSkill[];
  methodologies: WorkMethodology[];
  experiences: WorkRoleSkillMethodologyExperience[];
  languages: WorkLanguageLevel[];
  contactInfo: ContactInfo;
  professionalInfo: ProfessionalInfo;
  serviceInfo: ServiceInfo;
  cvFile?: FileDataViewModel | null;
  linkedinUrl?: string;
  contactAgent?: ContactAgent;
}

export interface OnBoardingStepProps {
  data: OnBoardingPersonProfile;
  loading: boolean;
  setLoading: (next: boolean) => void;
  onChange: (next: Partial<OnBoardingPersonProfile>) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubStepsChanged?: (total: number, current?: number) => void;
  subStep?: number;
  autocompletedFieldsAlert?: JSX.Element | null;
}

export interface WorkRole {
  id: string;
  label: string;
  order?: number;
}

export interface WorkRoleSkill {
  roleId: string;
  id?: string;
  label: string;
  order?: number;
}

export interface WorkMethodology {
  id?: string;
  label: string;
  order?: number;
}

export interface WorkRoleSkillMethodologyExperience {
  role: string;
  roleId: string;
  skill: string;
  skillId?: string;
  methodologyId?: string;
  methodology?: string;
  code: string;
  ge: number;
  le?: number;
  favorite: boolean;
}

/** Identifies a row across rebuilds: a role row, one of its skills, or a methodology. */
export const experienceRowKey = (e: WorkRoleSkillMethodologyExperience) => `${e.methodologyId ?? ''}|${e.roleId ?? ''}|${e.skill ?? ''}`;

/**
 * Every field a row carries, declared as a Record over the type so that adding a field to
 * `WorkRoleSkillMethodologyExperience` fails the build right here. Without that, a new field would
 * simply be left out of the comparison below and the symptom would be an answer the person gave
 * being discarded in silence.
 */
const EXPERIENCE_ROW_FIELDS: Record<keyof WorkRoleSkillMethodologyExperience, true> = {
  role: true,
  roleId: true,
  skill: true,
  skillId: true,
  methodologyId: true,
  methodology: true,
  code: true,
  ge: true,
  le: true,
  favorite: true
};

/**
 * Two rows carry the same answer. Compared field by field and NEVER by identity: the experience step
 * rebuilds its rows from `data`, which lags the live state by one render, so the rebuilt rows come
 * out equivalent to the ones already held but never as the same objects. An identity check there
 * never settles; it swaps one set for the other on every pass, each swap publishing through
 * `onChange` and bringing the next one round, which is the endless render loop that ended in
 * «Maximum update depth exceeded». Pinned by `types.test.ts`.
 */
const EXPERIENCE_ROW_FIELD_NAMES = Object.keys(EXPERIENCE_ROW_FIELDS) as (keyof WorkRoleSkillMethodologyExperience)[];

export const sameExperienceRow = (a: WorkRoleSkillMethodologyExperience, b: WorkRoleSkillMethodologyExperience) =>
  EXPERIENCE_ROW_FIELD_NAMES.every((field) => a[field] === b[field]);

export interface WorkLanguageLevel {
  language: string;
  languageId: number;
  level: LanguageLevel;
  order?: number;
}

export interface ContactInfo {
  username: string;
  phoneNumber: string;
  phoneNumberCountryId: number;
  phoneNumberCountryName: string;
}

export interface ProfessionalInfo {
  location: LocationInfo;
  relatedUrl?: string;
  shareRelatedUrl?: boolean;
}

export interface ServiceInfo {
  serviceDefinitionPhrase: string;
  serviceDefinition: string;
}

export const experienceOptions: { label: string; value: string; ge: number; le?: number }[] = [
  {
    label: '0 a 1',
    value: '0-1',
    ge: 0,
    le: 1
  },
  {
    label: '1 a 2',
    value: '1-2',
    ge: 1,
    le: 2
  },
  {
    label: '2 a 4',
    value: '2-4',
    ge: 2,
    le: 4
  },
  {
    label: '4 a 6',
    value: '4-6',
    ge: 4,
    le: 6
  },
  {
    label: '6 a 10',
    value: '6-10',
    ge: 6,
    le: 10
  },
  {
    label: '10 a 15',
    value: '10-15',
    ge: 10,
    le: 15
  },
  {
    label: '+15 años',
    value: '+15',
    ge: 15,
    le: undefined
  }
];

export const getExperienceCode = (e: Experience) => {
  if (e == null) return undefined;
  // eslint-disable-next-line eqeqeq
  return experienceOptions.find((x) => x.ge === e.yearsGreatEqThan && x.le == e.yearsLessEqThan)?.value;
};
